import { Ability } from '@casl/ability';
import {
  Actions,
  LocationForm,
  MultiSelectOption,
  ResourceForm,
  Subjects,
  UpsertResource,
} from '@dispatch/shared/types';
import { CreateNewEntityButton, TabPanel } from '@dispatch/web/ui';
import { Box, Grid, Toolbar } from '@mui/material';
import { useContext, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFieldArray,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { LocationInformation } from './LocationInformation';
import { LocationSidebar } from './LocationSidebar';
import { ResourceContext } from '../../ResourcesDetails';
import { ServiceInformation } from './ServiceInformation';
import { ExclusionZonesInformation } from './ExclusionZonesInformation';
import { red } from '@mui/material/colors';

export type LocationsProps = {
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceForm>;
  errors: FieldErrors;
  statesOptions: MultiSelectOption[];
  upsertResource: UpsertResource;
  resourceSetValue: UseFormSetValue<ResourceForm>;
  resourceErrors: FieldErrors;
  resourceFormIsDirty: boolean;
  isReadOnly?: boolean;
};

export function LocationsTab({
  control,
  permissions,
  watch,
  errors,
  statesOptions,
  upsertResource,
  resourceSetValue,
  resourceErrors,
  resourceFormIsDirty,
  isReadOnly = false,
}: LocationsProps) {
  const { fields, append } = useFieldArray({
    control,
    name: 'locations',
  });
  const [currentLocation, setLocation] = useState(0);
  const [selected, setSelected] = useState(0);
  const { data } = useContext(ResourceContext);
  const emptyLocationForm: LocationForm = {
    locationInformation: {
      name: `New Location`,
      street: '',
      unit: '',
      city: '',
      state: '',
      zip: '',
      county: '',
      isVirtualLocation: 'false',
    },
    serviceInformation: {
      startDate: new Date().toISOString(),
      endDate: '',
      allowedTravelRadius: 0,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
  };

  const selectLocation = (index: number) => {
    setLocation(index);
    setSelected(index);
  };

  const addLocation = () => {
    const newLocationIndex = fields.length;
    append(emptyLocationForm);
    selectLocation(newLocationIndex);
  };

  const onDeleteLocation = (locationIndex: number) => {
    const postBody = {
      ...data,
      locations: data?.locations?.filter(
        (location, index) => index !== locationIndex
      ),
    };
    upsertResource(postBody);
  };

  const renderLocations = (currentTab: number) =>
    fields.map((field, index) => {
      return (
        <TabPanel value={currentTab} index={index} key={field.id}>
          <Grid container item spacing={2}>
            <Grid container item xs={7}>
              <LocationInformation
                control={control}
                index={index}
                permissions={permissions}
                errors={errors?.[index]}
                statesOptions={statesOptions}
                disableStartLocation={fields.length <= 1}
                watch={watch}
                isReadOnly={isReadOnly}
              />
            </Grid>
            <Grid container item xs={5}>
              <ServiceInformation
                control={control}
                index={index}
                permissions={permissions}
                errors={errors?.[index]}
                watch={watch}
                resourceSetValue={resourceSetValue}
                isReadOnly={isReadOnly}
              />
            </Grid>
            <Grid container item xs={12}>
              <ExclusionZonesInformation
                control={control}
                index={index}
                permissions={permissions}
                errors={errors?.[index]}
                isReadOnly={isReadOnly}
              />
            </Grid>
          </Grid>
        </TabPanel>
      );
    });

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Grid container spacing={2} alignItems="start">
        <Grid
          item
          xs={3}
          style={{
            border: resourceErrors?.['locations']
              ? `1px solid ${red[500]}`
              : undefined,
          }}
        >
          <LocationSidebar
            isReadOnly={isReadOnly}
            selected={selected}
            setSelected={setSelected}
            currentLocation={currentLocation}
            setLocation={setLocation}
            permissions={permissions}
            watch={watch}
            onDeleteLocation={onDeleteLocation}
            resourceFormIsDirty={resourceFormIsDirty}
            resourceSetValue={resourceSetValue}
            addLocation={addLocation}
          />
          <p
            style={{
              padding: 0,
              margin: 0,
              color: red[800],
            }}
          >
            {resourceErrors?.['locations']?.message || ''}
          </p>
        </Grid>
        <Grid container item xs={9}>
          {renderLocations(currentLocation)}
        </Grid>
      </Grid>
    </div>
  );
}
